import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    DialogContent,
    DialogTitle,
    DialogContentText,
    DialogActions,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    IconButton,
    MenuItem,
    RadioGroup,
    Radio,
    Select,
    Stack,
    Typography
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import { logToSentry } from "../../../../../config/Sentry";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../../../../common/components/Loading";
import { useAssetTypeResolutionReasons } from "../../../../../common/hooks";
import { enums, helpers } from "../../../../../common/lib";
import { strings } from "../../../../../common/i18n";
import actions from "../../../../../Store/actions";
import styles from "./styles";

const ResolveDialog = ({ onClose, selectedAssets }) => {
    const [resolutionReason, setResolutionReason] = useState(null);
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const { altAssets, auditLocations, auth, locations } = useSelector(
        (state) => state
    );

    const resolutionReasons = useAssetTypeResolutionReasons(6);

    useEffect(() => {
        if (
            location === null &&
            auditLocations.data.length === 1 &&
            locations.data.length
        ) {
            const defaultLocation = helpers.getListItemForId(
                locations,
                auditLocations.data[0].location
            );
            setLocation(defaultLocation?.id || null);
        }
    }, [auditLocations, location, locations]);

    const handleLocationChange = (event) => {
        try {
            setLocation(event.target.value);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("Location change failed:", { error });
        }
    };

    const handleResolutionChange = (event) => {
        try {
            setResolutionReason(event.target.value);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("Resolution reason change failed:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.onChangeEdit", { name: "resolutionReason" })
                )
            );
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const updates = selectedAssets.map((id) => {
                const assetData = helpers.getListItemForId(altAssets, id);
                const { asset, auxiliary } = assetData?.suggestedMatch || {};
                return {
                    id,
                    asset: asset?.id || null,
                    assetAuxiliary: auxiliary?.id || null,
                    decisionUser: auth.account.id,
                    decisionDate: new Date().toISOString(),
                    location,
                    resolutionReason: parseInt(resolutionReason)
                };
            });

            await dispatch(
                actions.altAssets.create.bulkUpdatePostRequest(updates)
            );

            dispatch(actions.toast.create.success(strings("assetResolved")));
            onClose?.();
            setLoading(false);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("Resolve multiple assets failed:", { error });
        }
    };

    if (loading) return <Loading loadingText="updatingAsset" />;

    return (
        <>
            <DialogTitle>{strings("chooseResolutionReason")}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 3 }}>
                {/* Description */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {strings("messages.resolveReasonSelection", {
                        count: selectedAssets?.length
                    })}
                </Typography>

                {/* Static: Location Selector */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>{strings("location")}</InputLabel>
                    <Select
                        value={location || ""}
                        onChange={handleLocationChange}
                        required
                    >
                        {auditLocations.data.map((item) => {
                            const loc = helpers.getListItemForId(
                                locations,
                                item.location
                            );
                            return (
                                loc && (
                                    <MenuItem
                                        key={loc.id}
                                        value={item.location}
                                    >
                                        {loc.name}
                                    </MenuItem>
                                )
                            );
                        })}
                    </Select>
                </FormControl>

                {/* Scrollable: Resolution Reasons */}
                <Box
                    sx={{
                        maxHeight: 200,
                        overflowY: "auto",
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        p: 2,
                        bgcolor: "#fafafa"
                    }}
                >
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            name="resolution-reasons"
                            value={resolutionReason}
                            onChange={handleResolutionChange}
                        >
                            {resolutionReasons.map((item) => (
                                <FormControlLabel
                                    key={item.id}
                                    value={item.id}
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography variant="body2">
                                            {item.name}
                                        </Typography>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>
            </DialogContent>

            <DialogActions sx={{ m: 2 }}>
                <Button
                    variant="contained"
                    sx={styles.primaryButton}
                    onClick={handleSubmit}
                    disabled={!location || !resolutionReason}
                >
                    {strings("submit")}
                </Button>
            </DialogActions>
        </>
    );
};

export default ResolveDialog;
